/**
 * The privacy policy: what the site keeps, and in particular how roster
 * uploads and app connections are handled. Readable without an account.
 */
export default function Privacy() {
  return (
    <div className="privacy">
      <a href="/" className="logo" style={{ marginBottom: 24 }}>
        <span className="logo-mark" aria-hidden>Δ</span>
        DeltaPicks
      </a>
      <h1 className="page-title">Privacy</h1>
      <p className="page-sub">Last updated September 25, 2026.</p>

      <div className="prose">
        <h2>Your account</h2>
        <p>
          We store your email address and a one-way hash of your password (never the password itself), your
          bankroll and display settings, the bets you log, and the fantasy leagues and rosters you add. You can see
          only your own leagues, rosters and bets; nobody else&rsquo;s account can read them.
        </p>

        <h2>Roster screenshots and files</h2>
        <ul>
          <li>A screenshot is used only to read your roster, and is <strong>deleted right after it is read</strong>. It is never stored, and never used for anything else.</li>
          <li>Before reading, we remove the photo&rsquo;s metadata (EXIF: location and device).</li>
          <li>The image is read by an AI model (Anthropic&rsquo;s Claude, through its API). Anthropic&rsquo;s API does not train on API inputs by default.</li>
          <li>Names or usernames of league-mates that appear in a screenshot are ignored and never saved. Only your own players are kept, once you press Save.</li>
          <li>CSV and Excel files are read directly on our server (no AI) and discarded the same way.</li>
          <li>We keep a small record of each upload — when, how, how many players were found and matched — but not the image or file.</li>
        </ul>

        <h2>Connected apps</h2>
        <ul>
          <li><strong>Sleeper:</strong> you give us your Sleeper username; we store your Sleeper user id and read your leagues through Sleeper&rsquo;s public, read-only API. No password.</li>
          <li><strong>Yahoo:</strong> you sign in on Yahoo&rsquo;s own page and approve read-only access. We store only the access tokens Yahoo gives us, encrypted. We never see your Yahoo password.</li>
          <li><strong>ESPN, NFL, CBS and others:</strong> we don&rsquo;t connect to them, and we never ask for another site&rsquo;s password or login cookies. Use a screenshot or a file export instead.</li>
          <li>We can&rsquo;t change anything in those apps — access is read-only.</li>
        </ul>

        <h2>Alerts</h2>
        <p>
          Start/sit alerts reach you in the app, by email, and — if you turn them on — as push notifications on the
          devices you allow. You can mute them per league on the My Team page. A push subscription is stored only so
          we can reach that device; turning notifications off removes it.
        </p>

        <h2>Disconnecting and deleting</h2>
        <ul>
          <li><strong>Disconnect a league</strong> (League tab): it stops syncing from Sleeper or Yahoo; the roster stays and becomes yours to edit.</li>
          <li><strong>Delete a league</strong> (League tab): the league, every roster in it, its alerts and saved trades are removed.</li>
          <li>To delete your account and everything in it, email us from the address you signed up with.</li>
        </ul>

        <h2>Betting information</h2>
        <p>DeltaPicks is a research tool. It does not place bets, and it does not share your ledger with anyone.</p>
      </div>
    </div>
  );
}
